import React from 'react'
import { useTranslation } from 'react-i18next'

import { BaseView, BaseText } from '@/components/base'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { useTheme } from '@/hooks/useTheme'

interface LoadingSetupProfileFormProps {
  variant?: 'spinner' | 'skeleton'
}

export const LoadingSetupProfileForm: React.FC<
  LoadingSetupProfileFormProps
> = ({ variant = 'spinner' }) => {
  const { t } = useTranslation()
  const theme = useTheme()

  if (variant === 'spinner') {
    return (
      <BaseView
        flex={1}
        center
        bgColor="background.screen"
        className="h-screen"
      >
        <BaseView gap={20} center>
          <LoadingSpinner size="large" />
          <BaseText
            fontSize={16}
            fontFamily="regular"
            color="label.description"
            textAlign="center"
          >
            {'Loading profile...'}
          </BaseText>
        </BaseView>
      </BaseView>
    )
  }

  // Skeleton variant - mimics the form structure
  const SkeletonBox: React.FC<{
    width: string | number
    height: string | number
    borderRadius?: string | number
    className?: string
  }> = ({ width, height, borderRadius = '8px', className = '' }) => (
    <div
      className={`animate-pulse ${className}`}
      style={{
        width,
        height,
        backgroundColor:
          theme.colors.skeleton?.backgroundColor ||
          theme.colors.background.secondaryGray + '40',
        borderRadius,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: '-100%',
          width: '100%',
          height: '100%',
          background: `linear-gradient(90deg, transparent, ${
            theme.colors.skeleton?.foregroundColor || 'rgba(255,255,255,0.1)'
          }, transparent)`,
          animation: 'skeleton-shimmer 1.5s infinite',
        }}
      />
    </div>
  )

  return (
    <BaseView gap={36} center className="flex-1 justify-center px-6">
      <style>
        {`
          @keyframes skeleton-shimmer {
            0% { left: -100%; }
            100% { left: 100%; }
          }
        `}
      </style>

      {/* Header skeleton */}
      <BaseView gap={28} center>
        <SkeletonBox width={200} height={36} />
      </BaseView>

      {/* Profile Form skeleton */}
      <BaseView gap={20} className="w-full max-w-md">
        {/* Photo skeleton */}
        <div className="flex justify-center mb-6">
          <SkeletonBox
            width={96}
            height={96}
            borderRadius="50%"
            className="rounded-full"
          />
        </div>

        {/* Form fields skeleton */}
        <BaseView gap={12}>
          {/* First Name */}
          <BaseView gap={4}>
            <SkeletonBox width={100} height={16} />
            <SkeletonBox width="100%" height={48} />
          </BaseView>

          {/* Last Name */}
          <BaseView gap={4}>
            <SkeletonBox width={100} height={16} />
            <SkeletonBox width="100%" height={48} />
          </BaseView>

          {/* Username */}
          <BaseView gap={4}>
            <SkeletonBox width={80} height={16} />
            <SkeletonBox width="100%" height={48} />
          </BaseView>

          {/* Email (optional) */}
          <BaseView gap={4}>
            <SkeletonBox width={60} height={16} />
            <SkeletonBox width="100%" height={48} />
          </BaseView>

          {/* Referral code link */}
          <SkeletonBox width={140} height={20} />
        </BaseView>
      </BaseView>

      {/* Submit Button skeleton */}
      <BaseView className="w-full max-w-md">
        <SkeletonBox width="100%" height={48} borderRadius="12px" />
      </BaseView>
    </BaseView>
  )
}

export default LoadingSetupProfileForm
